/** Current weight, body fat and lean mass, with the ways to update them. */
import { useState, type ReactElement } from 'react';
import { formatDayShort } from '../../lib/dates';
import { BODY_SOURCE_LABEL } from '../../lib/labels';
import { massFromKg, massUnit } from '../../lib/units';
import { useApp } from '../../state/app';
import { BodyCompModal } from '../BodyCompModal';
import { Icon } from '../Icon';
import { Card } from '../ui';

export function BodyCompCard(): ReactElement {
  const { profile, energy } = useApp();
  const [open, setOpen] = useState<null | 'number' | 'tape'>(null);
  const units = profile.unit_system;
  const c = energy.comp;
  const m = (kg: number | null): string => (kg === null ? '—' : `${massFromKg(kg, units).toFixed(1)} ${massUnit(units)}`);
  const fat: number | null = c.weightKg !== null && c.leanMassKg !== null ? c.weightKg - c.leanMassKg : null;
  const how: string | null = c.compSource === null ? null
    : c.compSource === 'lifting_profile' ? 'from Mighty Peanut Lifting'
    : `${c.compSource === 'estimate' ? 'Tape estimate' : BODY_SOURCE_LABEL[c.compSource]}${c.compDate ? `, ${formatDayShort(c.compDate)}` : ''}`;

  return (
    <Card title="Body composition" sub="used by your BMR, protein target and goal">
      <div className="comp-grid">
        <div><span>Weight</span><b className="num">{m(c.weightKg)}</b><small className="faint">{c.weightDate ? formatDayShort(c.weightDate) : 'no weigh-ins yet'}</small></div>
        <div><span>Body fat</span><b className="num">{c.bodyFatPct !== null ? `${c.bodyFatPct.toFixed(1)}%` : '—'}</b><small className="faint">{how ?? 'not measured yet'}</small></div>
        <div><span>Lean mass</span><b className="num">{m(c.leanMassKg)}</b><small className="faint">{c.leanMeasured ? 'measured' : c.leanMassKg !== null ? 'from body fat %' : ''}</small></div>
        <div><span>Fat mass</span><b className="num">{m(fat)}</b><small className="faint" /></div>
      </div>
      <div className="row" style={{ marginTop: 14 }}>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setOpen('number')}><Icon name="plus" size={15} /> Update body fat or lean mass</button>
        <button type="button" className="btn btn-sm" onClick={() => setOpen('tape')}><Icon name="scale" size={15} /> Measure with a tape</button>
      </div>
      {c.bodyFatPct === null && <p className="faint" style={{ margin: '10px 0 0', fontSize: 13, fontWeight: 600 }}>No DEXA or Bod Pod? The tape-measure (US Navy) method gives a reasonable estimate in two minutes.</p>}
      {open && <BodyCompModal initialTab={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}
